import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { translateErrorCategory } from "@/utils/errorTranslation";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  PlusCircle, 
  Trash2, 
  HelpCircle, 
  ArrowLeft, 
  X, 
  Sparkles, 
  AlertTriangle, 
  Loader2, 
  Check, 
  Filter, 
  Clock, 
  ArrowUpDown, 
  CheckCircle2, 
  Trophy, 
  BookOpen, 
  Tag, 
  SlidersHorizontal, 
  Layers,
  ChevronDown
} from "lucide-react";
import { ContextModal } from "@/components/ui/ContextModal";
import { errorBankService } from "@/services/errorBankService";
import { flashcardService } from "@/services/flashcardService";
import { useStudyTimeTracker } from "@/hooks/useStudyTimeTracker";


// Helper functions for metadata extraction and safety
function isWritingV2Error(error: any): boolean {
  return (
    error?.source === "ee_v2_pedagogical_correction" ||
    error?.metadata?.writingVersion === 2 ||
    error?.metadata?.createdFrom === "writing_v2" ||
    error?.metadata?.createdFrom === "writing_v2_error" ||
    String(error?.metadata?.createdFrom || "").startsWith("writing_v2_")
  );
}

function getErrorOriginal(error: any): string {
  return error.originalText || error.original || error.metadata?.original || "";
}

function getErrorCorrected(error: any): string {
  return error.correctedText || error.corrected || error.correction || error.metadata?.corrected || "";
}

function getErrorContext(error: any): string {
  return error.contextSentence || error.context || error.metadata?.contextSentence || "";
}

function getErrorExplanation(error: any): string {
  return error.explanation || error.metadata?.explanation || "";
}

function getErrorSection(error: any): string {
  return error.section || error.metadata?.section || "N/A";
}

function getErrorCategory(error: any): string {
  return error.category || error.metadata?.category || "Outro";
}

function getErrorSeverity(error: any): string {
  return error.severity || error.metadata?.severity || "medium";
}

function translateSeverity(severity: string, t?: any): string {
  if (!t) {
    const s = String(severity).toLowerCase();
    if (s === "high") return "Alta";
    if (s === "medium") return "Média";
    if (s === "low") return "Baixa";
    return severity || "Média";
  }
  const s = String(severity).toLowerCase().trim();
  const key = `writingErrors.severities.${s}`;
  const translated = t(key);
  return translated !== key ? translated : severity;
}

function translateCategory(category: string, t?: any): string {
  return translateErrorCategory(category, t || ((k: string) => k));
}

export default function WritingErrors() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get("category");

  const [errors, setErrors] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useStudyTimeTracker({
    activityType: "error_bank",
    competency: "writing",
    enabled: !isLoading
  });

  const [createdFlashcards, setCreatedFlashcards] = useState<Record<string, boolean>>({});

  const [creatingFlashcardId, setCreatingFlashcardId] = useState<string | null>(null);

  // Filter States
  const [filterSource, setFilterSource] = useState<string>("all");
  const [filterSection, setFilterSection] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>(selectedCategory || "all");
  const [filterSeverity, setFilterSeverity] = useState<string>("all");
  const [filterStudyStatus, setFilterStudyStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("newest");

  // Selection & Pagination States
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Context Truncation Expanded States
  const [expandedContexts, setExpandedContexts] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setCurrentPage(1);
  }, [filterSource, filterSection, filterCategory, filterSeverity, filterStudyStatus, sortBy]);

  useEffect(() => {
    if (selectedCategory) {
      setFilterCategory(selectedCategory);
    }
  }, [selectedCategory]);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(t("writingErrors.confirmDeleteErrors", { count: selectedIds.length }))) return;

    setIsDeletingBulk(true);
    console.log("[ERROR_BANK_BULK_DELETE_START]", {
      count: selectedIds.length,
      selectedIds,
    });

    try {
      const deletedCount = await errorBankService.deleteMultipleErrors(selectedIds);
      console.log("[ERROR_BANK_BULK_DELETE_SUCCESS]", {
        count: deletedCount,
      });
      setSelectedIds([]);
      await fetchErrors(true);
    } catch (err: any) {
      console.error("[ERROR_BANK_BULK_DELETE_ERROR]", err);
      alert(t("writingErrors.deleteMultipleErrorsError", { error: err.message || "Erro desconhecido" }));
    } finally {
      setIsDeletingBulk(false);
    }
  };

  const fetchErrors = async (isSilent = false) => {
    try {
      if (!isSilent) setIsLoading(true);
      const list = await errorBankService.getErrors();
      setErrors(list);

      try {
        const flashcards = await flashcardService.getFlashcards();
        const mapping: Record<string, boolean> = {};
        flashcards.forEach(fc => {
          if (fc.sourceErrorId) {
            mapping[fc.sourceErrorId] = true;
          }
        });
        setCreatedFlashcards(mapping);
      } catch (fErr) {
        console.error("Error loading flashcards mapping for error bank:", fErr);
      }
    } catch (err) {
      console.error("Error loading error bank:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchErrors();
  }, []);

  // Telemetry Audit Log for EE v2 Errors
  useEffect(() => {
    if (errors.length > 0) {
      console.log("[ERROR_BANK_EE_V2_ERRORS_FOUND]", errors.filter(isWritingV2Error).map(error => ({
        id: error.id,
        section: getErrorSection(error),
        category: getErrorCategory(error),
        severity: getErrorSeverity(error),
        hasOriginal: Boolean(getErrorOriginal(error)),
        hasCorrected: Boolean(getErrorCorrected(error)),
        hasExplanation: Boolean(getErrorExplanation(error)),
      })));
    }
  }, [errors]);

  useEffect(() => {
    const refreshErrors = () => {
      fetchErrors(true);
    };
    window.addEventListener("errorsUpdated", refreshErrors);
    window.addEventListener("dashboardUpdated", refreshErrors);
    return () => {
      window.removeEventListener("errorsUpdated", refreshErrors);
      window.removeEventListener("dashboardUpdated", refreshErrors);
    };
  }, []);

  // Modal states for manual error creation
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedError, setSelectedError] = useState<any | null>(null);
  const [newOriginal, setNewOriginal] = useState("");
  const [newCorrection, setNewCorrection] = useState("");
  const [newContext, setNewContext] = useState("");
  const [newExplanation, setNewExplanation] = useState("");
  const [newCategory, setNewCategory] = useState("Concordância");
  const [newSeverity, setNewSeverity] = useState("medium");
  const [newSection, setNewSection] = useState("N/A");

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("writingErrors.confirmDeleteError"))) return;
    try {
      await errorBankService.deleteError(id);
      fetchErrors(true);
    } catch (err: any) {
      console.error("Error deleting error record:", err);
      let errMsg = t("writingErrors.deleteErrorError", { error: err.message || "Erro desconhecido." });
      if (err.message?.includes("authenticated") || err.message?.includes("autenticado") || err.message?.includes("security") || err.message?.includes("JWT") || err.status === 401 || err.status === 403) {
        errMsg = t("writingErrors.sessionExpiredDelete");
      }
      alert(errMsg);
    }
  };

  const handleCreateFlashcard = async (err: any) => {
    if (creatingFlashcardId) return;
    setCreatingFlashcardId(err.id);

    console.log("[ERROR_BANK_EE_V2_CREATE_FLASHCARD_CLICK]", {
      id: err.id,
      section: getErrorSection(err),
      category: getErrorCategory(err),
    });

    try {
      await errorBankService.createFlashcardFromError(err.id);
      setCreatedFlashcards(prev => ({ ...prev, [err.id]: true }));
      alert(t("writingErrors.flashcardCreatedSuccess"));
    } catch (ex: any) {
      console.error("Error creating flashcard:", ex);
      if (ex.message && ex.message.includes("já existe")) {
        alert(t("writingErrors.flashcardExists"));
      } else if (ex.message?.includes("authenticated") || ex.message?.includes("autenticado") || ex.message?.includes("security") || ex.status === 401 || ex.status === 403) {
        alert(t("writingErrors.sessionExpired"));
      } else {
        alert(t("writingErrors.createFlashcardError", { error: ex.message || "Erro desconhecido." }));
      }
    } finally {
      setCreatingFlashcardId(null);
    }
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOriginal || !newCorrection) return;
    if (isSaving) return;

    const isLimitedPlan = !profile?.plan || ["free", "trial", "expired"].includes(profile.plan);
    if (isLimitedPlan && errors.length >= 50) {
      alert(t("writingErrors.limitReached"));
      navigate("/premium");
      setIsAddOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      await errorBankService.createError({
        original: newOriginal,
        correction: newCorrection,
        context: newContext || newOriginal,
        explanation: newExplanation || "Correção gramatical sugerida para melhor clareza e precisão no exame TEF.",
        category: newCategory,
        severity: newSeverity,
        section: newSection === "N/A" ? "" : newSection,
        competency: "writing"
      });

      await fetchErrors(true);

      setNewOriginal("");
      setNewCorrection("");
      setNewContext("");
      setNewExplanation("");
      setNewCategory("Concordância");
      setNewSeverity("medium");
      setNewSection("N/A");
      setIsAddOpen(false);
    } catch (err: any) {
      console.error("Error creating error record:", err);
      if (err.message === "TRIAL_LIMIT_EXCEEDED: error_bank" || err.message?.includes("TRIAL_LIMIT_EXCEEDED")) {
        alert(t("writingErrors.limitReached"));
        navigate("/premium");
        setIsAddOpen(false);
      } else {
        let errMsg = t("writingErrors.saveErrorError", { error: err.message || "Erro desconhecido." });
        if (err.message?.includes("authenticated") || err.message?.includes("autenticado") || err.message?.includes("security") || err.message?.includes("JWT") || err.status === 401 || err.status === 403) {
          errMsg = t("writingErrors.sessionExpired");
        }
        alert(errMsg);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Filter application logic
  const filteredErrors = errors.filter((err: any) => {
    // 1. Category Filter
    if (filterCategory !== "all") {
      const ec = getErrorCategory(err).trim().toLowerCase();
      const target = filterCategory.trim().toLowerCase();
      
      let matchesCategory = false;
      if (ec === target) matchesCategory = true;
      else if (target === "concordância" && (ec === "concordancia" || ec === "gramática" || ec === "gramatica")) matchesCategory = true;
      else if (target === "conjugação" && ec === "conjugacao") matchesCategory = true;
      else if (target === "vocabulário" && (ec === "vocabulario" || ec === "vocabulary")) matchesCategory = true;
      else if (target === "ortografia" && ec === "orthographe") matchesCategory = true;
      else if (target === "preposição" && (ec === "preposições" || ec === "preposicoes" || ec === "preposition" || ec === "prepositions")) matchesCategory = true;
      
      if (!matchesCategory) return false;
    }

    // 2. Source Filter
    if (filterSource !== "all") {
      const isV2 = isWritingV2Error(err);
      if (filterSource === "writing_v2" && !isV2) return false;
      if (filterSource === "other" && isV2) return false;
    }

    // 3. Section Filter
    if (filterSection !== "all") {
      const sec = getErrorSection(err).trim().toLowerCase();
      const target = filterSection.trim().toLowerCase();
      if (sec !== target) return false;
    }

    // 4. Severity Filter
    if (filterSeverity !== "all") {
      const sev = getErrorSeverity(err).trim().toLowerCase();
      const target = filterSeverity.trim().toLowerCase();
      let targetEng = "medium";
      if (target === "alta") targetEng = "high";
      if (target === "média" || target === "media") targetEng = "medium";
      if (target === "baixa") targetEng = "low";
      if (sev !== targetEng) return false;
    }

    return true;
  });

  // Sorting logic
  const sortedErrors = [...filteredErrors].sort((a, b) => {
    if (sortBy === "newest") {
      const dateA = new Date(a.createdAt || a.created_at || 0).getTime();
      const dateB = new Date(b.createdAt || b.created_at || 0).getTime();
      return dateB - dateA;
    }
    if (sortBy === "oldest") {
      const dateA = new Date(a.createdAt || a.created_at || 0).getTime();
      const dateB = new Date(b.createdAt || b.created_at || 0).getTime();
      return dateA - dateB;
    }
    if (sortBy === "severity") {
      const getSeverityScore = (err: any) => {
        const sev = getErrorSeverity(err).toLowerCase();
        if (sev === "high") return 3;
        if (sev === "medium") return 2;
        if (sev === "low") return 1;
        return 0;
      };
      return getSeverityScore(b) - getSeverityScore(a);
    }
    if (sortBy === "category") {
      const catA = translateCategory(getErrorCategory(a), t).toLowerCase();
      const catB = translateCategory(getErrorCategory(b), t).toLowerCase();
      return catA.localeCompare(catB);
    }
    return 0;
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedErrors.length / itemsPerPage);
  const paginatedErrors = sortedErrors.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Statistics summaries
  const totalCount = errors.length;
  const writingV2Count = errors.filter(isWritingV2Error).length;
  const highSeverityCount = errors.filter(e => getErrorSeverity(e).toLowerCase() === "high").length;
  const sectionACount = errors.filter(e => getErrorSection(e).toLowerCase().includes("section a")).length;
  const sectionBCount = errors.filter(e => getErrorSection(e).toLowerCase().includes("section b")).length;

  const getMostFrequentCategory = () => {
    if (errors.length === 0) return t("writingErrors.noCategory");
    const counts: Record<string, number> = {};
    errors.forEach(e => {
      const cat = translateCategory(getErrorCategory(e), t);
      counts[cat] = (counts[cat] || 0) + 1;
    });
    let maxCat = "";
    let maxCount = -1;
    Object.entries(counts).forEach(([cat, count]) => {
      if (count > maxCount) {
        maxCount = count;
        maxCat = cat;
      }
    });
    return maxCat || t("writingErrors.noCategory");
  };

  const toggleContextExpand = (id: string, contextVal: string) => {
    if (contextVal.length > 500) {
      console.warn("[ERROR_BANK_CONTEXT_TRUNCATED]", {
        id,
        originalLength: contextVal.length
      });
    }
    setExpandedContexts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 rounded-lg gap-1.5 font-semibold text-xs self-start"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Button>

        <Button 
          onClick={() => setIsAddOpen(true)}
          className="bg-primary hover:bg-primary/90 text-white rounded-xl shadow-md font-bold text-xs"
        >
          <PlusCircle className="w-4 h-4 mr-1.5" />
          {t("writingErrors.registerErrorButton")}
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t("writingErrors.title")}</h1>
        <p className="text-gray-500 mt-1">{t("writingErrors.subtitle")}</p>
      </div>

      {/* SUMMARY STATS DASHBOARD */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card className="border-none shadow-sm bg-gradient-to-br from-rose-50/50 to-white overflow-hidden rounded-2xl">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center text-rose-600 shrink-0">
              <AlertTriangle className="w-5 h-5 stroke-[2]" />
            </div>
            <div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("writingErrors.totalErrors")}</span>
              <span className="text-2xl font-black text-rose-900">{totalCount}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-purple-50/50 to-white overflow-hidden rounded-2xl">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 shrink-0">
              <Sparkles className="w-5 h-5 stroke-[2]" />
            </div>
            <div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("writingErrors.expressionEcr")}</span>
              <span className="text-2xl font-black text-purple-900">{writingV2Count}</span>
              <span className="text-[9px] text-purple-700 font-bold block mt-0.5">
                Sec. A: {sectionACount} · Sec. B: {sectionBCount}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-amber-50/50 to-white overflow-hidden rounded-2xl">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
              <Trophy className="w-5 h-5 stroke-[2]" />
            </div>
            <div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("writingErrors.frequentCat")}</span>
              <span className="text-sm font-black text-amber-900 truncate block max-w-[150px]" title={getMostFrequentCategory()}>
                {getMostFrequentCategory()}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-red-50/50 to-white overflow-hidden rounded-2xl">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-red-600 shrink-0">
              <Clock className="w-5 h-5 stroke-[2]" />
            </div>
            <div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("writingErrors.severeErrors")}</span>
              <span className="text-2xl font-black text-red-900">{highSeverityCount}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* FILTER CONTROL PANEL */}
      <Card className="border-none shadow-sm bg-white rounded-2xl p-5">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-50">
            <SlidersHorizontal className="w-4 h-4 text-primary" />
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t("writingErrors.filterTitle")}</h3>
          </div>

          <div className="grid gap-4 grid-cols-2 md:grid-cols-5">
            {/* Filter Source */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t("writingErrors.origin")}</Label>
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="w-full bg-gray-50 text-gray-800 text-xs font-semibold rounded-xl border-none p-2.5 h-[38px] focus:ring-1 focus:ring-primary cursor-pointer"
              >
                <option value="all">{t("writingErrors.allOrigins")}</option>
                <option value="writing_v2">{t("writingErrors.expressionEcr")}</option>
                <option value="other">{t("writingErrors.otherOrigins")}</option>
              </select>
            </div>

            {/* Filter Section */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t("writingErrors.sectionLabel")}</Label>
              <select
                value={filterSection}
                onChange={(e) => setFilterSection(e.target.value)}
                className="w-full bg-gray-50 text-gray-800 text-xs font-semibold rounded-xl border-none p-2.5 h-[38px] focus:ring-1 focus:ring-primary cursor-pointer"
              >
                <option value="all">{t("writingErrors.allSections")}</option>
                <option value="section a">{t("writingErrors.sectionA")}</option>
                <option value="section b">{t("writingErrors.sectionB")}</option>
              </select>
            </div>

            {/* Filter Category */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t("writingErrors.category")}</Label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full bg-gray-50 text-gray-800 text-xs font-semibold rounded-xl border-none p-2.5 h-[38px] focus:ring-1 focus:ring-primary cursor-pointer"
              >
                <option value="all">{t("writingErrors.allCategories")}</option>
                <option value="Gramática">{t("writingErrors.categories.grammaire")}</option>
                <option value="Preposição">{t("writingErrors.categories.preposition")}</option>
                <option value="Conjugação">{t("writingErrors.categories.conjugaison")}</option>
                <option value="Concordância">{t("writingErrors.categories.accord")}</option>
                <option value="Ortografia">{t("writingErrors.categories.orthographe")}</option>
                <option value="Pontuação">{t("writingErrors.categories.ponctuation")}</option>
                <option value="Vocabulário">{t("writingErrors.categories.vocabulaire")}</option>
                <option value="Sintaxe">{t("writingErrors.categories.syntaxe")}</option>
                <option value="Naturalidade">{t("writingErrors.categories.naturalité")}</option>
                <option value="Conectores">{t("writingErrors.categories.connecteurs")}</option>
                <option value="Pronomes">{t("writingErrors.categories.pronoms")}</option>
                <option value="Outro">{t("writingErrors.categories.outro")}</option>
              </select>
            </div>

            {/* Filter Severity */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t("writingErrors.severity")}</Label>
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="w-full bg-gray-50 text-gray-800 text-xs font-semibold rounded-xl border-none p-2.5 h-[38px] focus:ring-1 focus:ring-primary cursor-pointer"
              >
                <option value="all">{t("writingErrors.allSeverities")}</option>
                <option value="Alta">{t("writingErrors.severities.high")}</option>
                <option value="Média">{t("writingErrors.severities.medium")}</option>
                <option value="Baixa">{t("writingErrors.severities.low")}</option>
              </select>
            </div>

            {/* Sort Order */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t("writingErrors.sorting")}</Label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full bg-gray-50 text-gray-800 text-xs font-semibold rounded-xl border-none p-2.5 h-[38px] focus:ring-1 focus:ring-primary cursor-pointer"
              >
                <option value="newest">{t("writingErrors.sortNewest")}</option>
                <option value="oldest">{t("writingErrors.sortOldest")}</option>
                <option value="severity">{t("writingErrors.sortSeverity")}</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      {selectedCategory && (
        <div className="bg-rose-50/80 border border-rose-200/50 rounded-xl p-4 flex items-center justify-between animate-in slide-in-from-top duration-300">
          <div className="flex items-center gap-2 text-sm text-rose-800 font-semibold">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
            <span>{t("writingErrors.activeCategoryFilter", { category: translateCategory(selectedCategory, t) })}</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setSearchParams({});
              setFilterCategory("all");
            }}
            className="rounded-lg text-xs hover:bg-rose-100 text-rose-800 font-bold"
          >
            {t("writingErrors.clearFilter")}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="py-24 text-center text-gray-400 flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-100 shadow-sm animate-pulse">
          <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
          <h4 className="font-bold text-gray-700 text-base">{t("writingErrors.loadingTitle")}</h4>
          <p className="text-xs text-gray-500 mt-1">{t("writingErrors.loadingDesc")}</p>
        </div>
      ) : sortedErrors.length === 0 ? (
        <div className="py-16 text-center text-gray-400 flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-100 shadow-sm">
          <Sparkles className="w-12 h-12 mb-3 stroke-[1.2] text-gray-300 animate-pulse" />
          <h4 className="font-semibold text-gray-700 text-sm">{t("writingErrors.noErrorsTitle")}</h4>
          <p className="text-xs text-gray-500 mt-1 max-w-sm">
            {t("writingErrors.noErrorsDesc")}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-red-50/30 border border-red-100/50 p-4 rounded-2xl flex flex-wrap gap-4 items-center justify-between animate-in fade-in duration-300">
            <div className="flex items-center gap-3">
              <input 
                type="checkbox"
                checked={paginatedErrors.length > 0 && paginatedErrors.every(e => selectedIds.includes(e.id))}
                ref={el => {
                  if (el) {
                    const allSelected = paginatedErrors.length > 0 && paginatedErrors.every(e => selectedIds.includes(e.id));
                    const someSelected = paginatedErrors.some(e => selectedIds.includes(e.id));
                    el.indeterminate = someSelected && !allSelected;
                  }
                }}
                onChange={(e) => {
                  if (e.target.checked) {
                    const newIds = [...selectedIds];
                    paginatedErrors.forEach(errItem => {
                      if (!newIds.includes(errItem.id)) newIds.push(errItem.id);
                    });
                    setSelectedIds(newIds);
                  } else {
                    const paginatedIds = paginatedErrors.map(errItem => errItem.id);
                    setSelectedIds(selectedIds.filter(id => !paginatedIds.includes(id)));
                  }
                }}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 cursor-pointer"
              />
              <span className="text-xs font-bold text-gray-700">
                {selectedIds.length > 0 
                  ? t("writingErrors.selectedCountMsg", { count: selectedIds.length, total: sortedErrors.length }) 
                  : t("writingErrors.selectAllCountMsg", { count: paginatedErrors.length })
                }
              </span>
            </div>

            {selectedIds.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                disabled={isDeletingBulk}
                onClick={handleBulkDelete}
                className="rounded-xl font-bold text-xs h-9 px-4 shadow-sm transition-all duration-200"
              >
                {isDeletingBulk ? (
                  <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5 mr-2" />
                )}
                {t("writingErrors.deleteSelected", { count: selectedIds.length })}
              </Button>
            )}
          </div>

          {/* GRID OF ERROR STUDY CARDS */}
          <div className="grid gap-6 md:grid-cols-2">
            {paginatedErrors.map((err: any) => {
              const isChecked = selectedIds.includes(err.id);
              const orig = getErrorOriginal(err);
              const corr = getErrorCorrected(err);
              const exp = getErrorExplanation(err);
              const cat = getErrorCategory(err);
              const sec = getErrorSection(err);
              const sev = getErrorSeverity(err);
              const isV2 = isWritingV2Error(err);
              const rawCtx = getErrorContext(err);

              // Context Sentence Expand / Collapse logic
              const isExpanded = expandedContexts[err.id];
              const shouldTruncate = rawCtx.length > 500;
              const displayedCtx = shouldTruncate && !isExpanded ? rawCtx.slice(0, 500) + "..." : rawCtx;

              return (
                <Card 
                  key={err.id} 
                  onClick={() => setSelectedError(err)}
                  className={`border-none shadow-sm bg-white overflow-hidden flex flex-col justify-between hover:shadow-md transition-all duration-200 cursor-pointer animate-in fade-in duration-300 ${
                    isChecked ? 'ring-2 ring-red-500 bg-red-50/5' : ''
                  }`}
                >
                  <div>
                    <div className={`p-1 bg-gradient-to-r ${isV2 ? 'from-purple-500 to-indigo-500' : 'from-red-400 to-rose-400'}`} />
                    <CardHeader className="pt-6 pb-2">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <input 
                            type="checkbox"
                            checked={isChecked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(prev => [...prev, err.id]);
                              } else {
                                setSelectedIds(prev => prev.filter(id => id !== err.id));
                              }
                            }}
                            className="w-4 h-4 mt-1 text-red-600 border-gray-300 rounded focus:ring-red-500 cursor-pointer shrink-0"
                          />
                          <div className="flex flex-wrap items-center gap-1.5 flex-1">
                            <Badge variant="outline" className="text-red-700 bg-red-50/50 border-red-100 text-[10px] font-bold">
                              {translateCategory(cat, t)}
                            </Badge>
                            
                            {isV2 ? (
                              <Badge className="bg-gradient-to-r from-purple-600 to-indigo-500 text-white border-none text-[9px] font-bold">
                                {t("writingErrors.expressionEcr")}
                              </Badge>
                            ) : (
                              <Badge className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-none text-[9px] font-bold">
                                {t("writingErrors.origin")}: {t("writingErrors.otherOrigins")}
                              </Badge>
                            )}

                            {sec && sec !== "N/A" && (
                              <Badge className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-none text-[9px] font-bold">
                                {sec}
                              </Badge>
                            )}

                            {sev && (
                              <Badge className={`text-[9px] font-bold border-none ${
                                sev === "high" ? "bg-rose-100 text-rose-700 hover:bg-rose-200" :
                                sev === "medium" ? "bg-amber-100 text-amber-700 hover:bg-amber-200" :
                                "bg-blue-100 text-blue-700 hover:bg-blue-200"
                              }`}>
                                {translateSeverity(sev, t)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(err.id);
                          }}
                          className="h-8 w-8 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 shrink-0 -mt-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {/* Original Sentence Block */}
                      <div className="space-y-1.5">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("writingErrors.originalSentence")}</span>
                        <div className="p-3 bg-red-50/50 border border-red-50 rounded-xl text-base font-medium leading-relaxed font-sans text-red-900">
                          {orig}
                        </div>
                      </div>

                      {/* Corrected Sentence Block */}
                      <div className="space-y-1.5">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("writingErrors.correctedSentence")}</span>
                        <div className="p-3 bg-green-50/50 border border-green-50 rounded-xl text-base font-semibold leading-relaxed font-sans text-emerald-900">
                          {corr}
                        </div>
                      </div>

                      {/* Context block with safety expand/collapse */}
                      {rawCtx && (
                        <div className="space-y-1.5">
                          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("writingErrors.contextSentence")}</span>
                          <p className="text-xs text-gray-600 leading-relaxed italic bg-gray-50/50 p-3 rounded-xl border border-gray-100/50">
                            "{displayedCtx}"
                          </p>
                          {shouldTruncate && (
                            <Button
                              variant="link"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleContextExpand(err.id, rawCtx);
                              }}
                              className="p-0 h-auto text-[11px] font-bold text-primary hover:text-primary/80 mt-1 block self-start"
                            >
                              {isExpanded ? t("writingErrors.contextShowLess") : t("writingErrors.contextShowMore")}
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Grammar Explanation block */}
                      {exp && (
                        <div className="space-y-1 bg-gray-50 p-3.5 rounded-xl border border-gray-100 flex gap-2">
                          <HelpCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div className="space-y-0.5">
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("writingErrors.explanationTitle")}</span>
                            <p className="text-xs text-gray-600 leading-relaxed">{exp}</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </div>

                  <CardFooter className="pt-0 pb-6 px-6 flex flex-col gap-3">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateFlashcard(err);
                      }}
                      disabled={creatingFlashcardId === err.id || createdFlashcards[err.id]}
                      className="w-full bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary rounded-xl text-xs py-4.5 font-bold border border-primary/20"
                    >
                      {creatingFlashcardId === err.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("writingErrors.saving")}
                        </>
                      ) : createdFlashcards[err.id] ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-emerald-600" />
                          <span className="text-emerald-700 font-black">{t("testResult.flashcardCreated")}</span>
                        </>
                      ) : (
                        <>
                          <PlusCircle className="w-4 h-4 mr-2" />
                          {t("testResult.createFlashcardButton")}
                        </>
                      )}
                    </Button>

                    {/* Future Study Actions Inactive visual row */}
                    <div className="flex gap-2 w-full mt-1 pt-3 border-t border-gray-100/50 justify-between items-center">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("writingErrors.study")}</span>
                      <div className="flex gap-1.5">
                        <Button 
                          variant="outline"
                          size="sm"
                          disabled
                          className="text-[10px] h-7 px-2.5 rounded-lg border-gray-200/50 text-gray-400 font-bold bg-gray-50/50"
                          title={t("writingErrors.futurePhaseTooltip")}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-gray-300" />
                          {t("writingErrors.markReviewed")}
                        </Button>
                        <Button 
                          variant="outline"
                          size="sm"
                          disabled
                          className="text-[10px] h-7 px-2.5 rounded-lg border-gray-200/50 text-gray-400 font-bold bg-gray-50/50"
                          title={t("writingErrors.futurePhaseTooltip")}
                        >
                          <Trophy className="w-3.5 h-3.5 mr-1 text-gray-300" />
                          {t("writingErrors.markMastered")}
                        </Button>
                      </div>
                    </div>
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          {/* PAGINATION PANEL */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-100 pt-6">
              <span className="text-xs font-semibold text-gray-500">
                {t("writingErrors.paginationInfo", { current: currentPage, total: totalPages, count: sortedErrors.length })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  className="rounded-xl font-bold text-xs"
                >
                  {t("writingErrors.previous")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  className="rounded-xl font-bold text-xs"
                >
                  {t("writingErrors.next")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual Error Register Dialog Modal */}
      {isAddOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200">
          <Card className="max-w-md w-full bg-white border-none shadow-2xl rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <CardHeader className="bg-gray-50 border-b border-gray-100 pb-4 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold text-gray-900">{t("writingErrors.newErrorTitle")}</CardTitle>
                <CardDescription className="text-xs">{t("writingErrors.newErrorDesc")}</CardDescription>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsAddOpen(false)}
                className="h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-4.5 h-4.5" />
              </Button>
            </CardHeader>
            <form onSubmit={handleAddSubmit}>
              <CardContent className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="space-y-1.5">
                  <Label htmlFor="original" className="text-xs font-bold text-gray-500">{t("writingErrors.errorTextLabel")}</Label>
                  <Input 
                    id="original" 
                    value={newOriginal}
                    onChange={(e) => setNewOriginal(e.target.value)}
                    required
                    placeholder={t("writingErrors.placeholderOriginal")} 
                    className="bg-gray-50 border-none rounded-xl py-5"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label htmlFor="correction" className="text-xs font-bold text-gray-500">{t("writingErrors.correctedTextLabel")}</Label>
                  <Input 
                    id="correction" 
                    value={newCorrection}
                    onChange={(e) => setNewCorrection(e.target.value)}
                    required
                    placeholder={t("writingErrors.placeholderCorrection")} 
                    className="bg-gray-50 border-none rounded-xl py-5"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="context" className="text-xs font-bold text-gray-500">{t("writingErrors.contextLabel")}</Label>
                  <Input 
                    id="context" 
                    value={newContext}
                    onChange={(e) => setNewContext(e.target.value)}
                    placeholder={t("writingErrors.placeholderContext")} 
                    className="bg-gray-50 border-none rounded-xl py-5"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="category" className="text-[10px] font-bold text-gray-500 uppercase">{t("writingErrors.category")}</Label>
                    <select
                      id="category"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="w-full bg-gray-50 text-gray-800 text-xs font-medium rounded-xl border-none p-3 h-[42px] focus:ring-1 focus:ring-primary cursor-pointer"
                    >
                      <option value="Gramática">{t("writingErrors.categories.grammaire")}</option>
                      <option value="Preposição">{t("writingErrors.categories.preposition")}</option>
                      <option value="Conjugação">{t("writingErrors.categories.conjugaison")}</option>
                      <option value="Concordância">{t("writingErrors.categories.accord")}</option>
                      <option value="Ortografia">{t("writingErrors.categories.orthographe")}</option>
                      <option value="Pontuação">{t("writingErrors.categories.ponctuation")}</option>
                      <option value="Vocabulário">{t("writingErrors.categories.vocabulaire")}</option>
                      <option value="Sintaxe">{t("writingErrors.categories.syntaxe")}</option>
                      <option value="Naturalidade">{t("writingErrors.categories.naturalité")}</option>
                      <option value="Conectores">{t("writingErrors.categories.connecteurs")}</option>
                      <option value="Pronomes">{t("writingErrors.categories.pronoms")}</option>
                      <option value="Outro">{t("writingErrors.categories.outro")}</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="severity" className="text-[10px] font-bold text-gray-500 uppercase">{t("writingErrors.severity")}</Label>
                    <select
                      id="severity"
                      value={newSeverity}
                      onChange={(e) => setNewSeverity(e.target.value)}
                      className="w-full bg-gray-50 text-gray-800 text-xs font-medium rounded-xl border-none p-3 h-[42px] focus:ring-1 focus:ring-primary cursor-pointer"
                    >
                      <option value="low">{t("writingErrors.severities.low")}</option>
                      <option value="medium">{t("writingErrors.severities.medium")}</option>
                      <option value="high">{t("writingErrors.severities.high")}</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="section" className="text-[10px] font-bold text-gray-500 uppercase">{t("writingErrors.sectionLabel")}</Label>
                    <select
                      id="section"
                      value={newSection}
                      onChange={(e) => setNewSection(e.target.value)}
                      className="w-full bg-gray-50 text-gray-800 text-xs font-medium rounded-xl border-none p-3 h-[42px] focus:ring-1 focus:ring-primary cursor-pointer"
                    >
                      <option value="N/A">N/A</option>
                      <option value="Section A">{t("writingErrors.sectionA")}</option>
                      <option value="Section B">{t("writingErrors.sectionB")}</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="explanation" className="text-xs font-bold text-gray-500">{t("writingErrors.explanationLabel")}</Label>
                  <Textarea 
                    id="explanation" 
                    value={newExplanation}
                    onChange={(e) => setNewExplanation(e.target.value)}
                    placeholder={t("writingErrors.placeholderExplanation")} 
                    className="bg-gray-50 border-none rounded-xl min-h-[85px]"
                  />
                </div>
              </CardContent>
              
              <CardFooter className="bg-gray-50/50 border-t border-gray-100 p-4 flex gap-2 justify-end">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddOpen(false)}
                  className="rounded-xl border-gray-100 font-semibold text-xs"
                >
                  {t("common.cancel")}
                </Button>
                <Button 
                  type="submit"
                  disabled={isSaving}
                  className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs"
                >
                  {isSaving ? t("writingErrors.saving") : t("writingErrors.addErrorButton")}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      )}

      {/* DETAILED WRITING ERROR VIEW MODAL */}
      <ContextModal
        isOpen={!!selectedError}
        onClose={() => setSelectedError(null)}
        title={t("writingErrors.errorDetailsTitle")}
        description={t("writingErrors.errorDetailsDesc")}
        size="lg"
      >
        {selectedError && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <Badge variant="outline" className="text-red-700 bg-red-50 border-red-100 text-[10px] font-bold">
                {translateCategory(getErrorCategory(selectedError), t)}
              </Badge>

              {isWritingV2Error(selectedError) ? (
                <Badge className="bg-gradient-to-r from-purple-600 to-indigo-500 text-white border-none text-[9px] font-bold">
                  {t("writingErrors.expressionEcr")}
                </Badge>
              ) : (
                <Badge className="bg-purple-50 text-purple-700 border-none text-[9px] font-bold">
                  {t("writingErrors.origin")}: {t("writingErrors.otherOrigins")}
                </Badge>
              )}

              {getErrorSection(selectedError) && getErrorSection(selectedError) !== "N/A" && (
                <Badge className="bg-indigo-50 text-indigo-700 border-none text-[9px] font-bold">
                  {t("writingErrors.sectionLabel")}: {getErrorSection(selectedError)}
                </Badge>
              )}

              {getErrorSeverity(selectedError) && (
                <Badge className={`text-[9px] font-bold border-none ${
                  getErrorSeverity(selectedError) === "high" ? "bg-rose-100 text-rose-700 hover:bg-rose-200" :
                  getErrorSeverity(selectedError) === "medium" ? "bg-amber-100 text-amber-700 hover:bg-amber-200" :
                  "bg-blue-100 text-blue-700 hover:bg-blue-200"
                }`}>
                  {t("writingErrors.severity")}: {translateSeverity(getErrorSeverity(selectedError), t)}
                </Badge>
              )}
            </div>

            <div className="space-y-3.5">
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("writingErrors.originalSentence")}</span>
                <div className="p-3.5 bg-red-50/50 border border-red-50 rounded-xl text-base font-medium leading-relaxed font-sans text-red-900">
                  {getErrorOriginal(selectedError)}
                </div>
              </div>

              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("writingErrors.correctedSentence")}</span>
                <div className="p-3.5 bg-green-50/50 border border-green-50 rounded-xl text-base font-semibold leading-relaxed font-sans text-emerald-900">
                  {getErrorCorrected(selectedError)}
                </div>
              </div>

              {getErrorContext(selectedError) && (
                <div className="space-y-1.5">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("writingErrors.contextSentence")}</span>
                  <p className="text-xs text-gray-600 leading-relaxed italic font-medium bg-gray-50/50 p-3.5 rounded-xl border border-gray-100/50">
                    "{getErrorContext(selectedError)}"
                  </p>
                </div>
              )}

              {getErrorExplanation(selectedError) && (
                <div className="space-y-1.5 bg-gray-50 p-4 rounded-xl border border-gray-100 flex gap-2.5">
                  <HelpCircle className="w-4.5 h-4.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block">{t("writingErrors.explanationTitle")}</span>
                    <p className="text-xs text-gray-700 leading-relaxed font-medium">{getErrorExplanation(selectedError)}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-gray-100 flex gap-2 justify-end">
              <Button 
                variant="outline"
                onClick={() => setSelectedError(null)}
                className="rounded-xl border-gray-100 font-semibold text-xs"
              >
                {t("common.back")}
              </Button>
              
              {createdFlashcards[selectedError.id] ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-bold text-xs px-3.5 py-1.5 rounded-xl self-center">
                  {t("testResult.flashcardCreated")}
                </Badge>
              ) : (
                <Button 
                  onClick={async () => {
                    await handleCreateFlashcard(selectedError);
                    setSelectedError(null);
                  }}
                  disabled={creatingFlashcardId === selectedError.id}
                  className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs"
                >
                  {creatingFlashcardId === selectedError.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("writingErrors.saving")}
                    </>
                  ) : (
                    <>
                      <PlusCircle className="w-4 h-4 mr-2" />
                      {t("testResult.createFlashcardButton")}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </ContextModal>
    </div>
  );
}
